import { useEffect, useState, useCallback } from "react"
import { opsAdminApi, type OpsLlmFilters, type OpsLlmGroupedTotals, type OpsLlmGroupedRow } from "@/lib/opsAdminApi"

type RangePreset = "7d" | "30d" | "90d"
type GroupTab = "property" | "surface" | "user"

const SURFACE_LABELS: Record<string, string> = {
  display: "Display Messages",
  guidebook: "Guidebook",
  guidebook_highlights: "Guidebook Highlights",
  host_strategy_summary: "Strategy Summary",
  performance_insights: "Performance Insights",
  forecast_interpretation: "Forecast Interpretation",
  overview_intelligence: "Overview Intelligence",
  review_intelligence: "Review Intelligence",
  listing_audit: "Listing Audit",
  listing_content_gen: "Content Generator",
  listing_generate: "Content Generator",
  photo_analysis: "Photo Analysis",
  market_digest: "Market Digest",
  research_enrich: "Guide Research",
  admin_populate_ai: "Admin Populate AI",
  review_intel: "Review Intelligence",
  research: "Guide Research",
  fallback_populate: "Fallback Populate",
  forecast: "Revenue Forecast",
  strategy: "Strategy Overview",
}

const SURFACE_CATEGORIES: Record<string, string> = {
  display: "Guest",
  guidebook: "Guest",
  guidebook_highlights: "Guest",
  host_strategy_summary: "Host",
  performance_insights: "Host",
  forecast_interpretation: "Host",
  overview_intelligence: "Host",
  review_intelligence: "Host",
  listing_audit: "Host",
  listing_content_gen: "Host",
  photo_analysis: "Host",
  market_digest: "Ops",
  research_enrich: "Ops",
  admin_populate_ai: "Ops",
}

type UserRow = OpsLlmGroupedRow & { actor_type?: string; email?: string }

function buildWindow(preset: RangePreset): { from: number; to: number } {
  const now = Date.now()
  const days = preset === "7d" ? 7 : preset === "30d" ? 30 : 90
  return { from: now - days * 24 * 60 * 60 * 1000, to: now }
}

function usd(v: number): string {
  return v >= 0.01 ? `$${v.toFixed(2)}` : `$${v.toFixed(4)}`
}

function usd4(v: number): string {
  return `$${v.toFixed(4)}`
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

export function OpsCostBreakdownPage() {
  const [preset, setPreset] = useState<RangePreset>("30d")
  const [tab, setTab] = useState<GroupTab>("property")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [totals, setTotals] = useState<OpsLlmGroupedTotals | null>(null)
  const [rows, setRows] = useState<UserRow[]>([])

  const loadData = useCallback(async () => {
    setLoading(true)
    setError("")
    const { from, to } = buildWindow(preset)
    const filters: OpsLlmFilters = { from, to }
    try {
      let res: { ok: boolean; totals?: OpsLlmGroupedTotals; rows?: UserRow[] }
      if (tab === "property") {
        res = await opsAdminApi.getLlmByProperty(filters)
      } else if (tab === "surface") {
        res = await opsAdminApi.getLlmBySurface(filters)
      } else {
        res = await opsAdminApi.getLlmByUser(filters)
      }
      if (!res.ok) {
        setError("Failed to load cost data.")
        return
      }
      setTotals(res.totals ?? null)
      setRows(res.rows ?? [])
    } catch {
      setError("Network error loading cost data.")
    } finally {
      setLoading(false)
    }
  }, [preset, tab])

  useEffect(() => { loadData() }, [loadData])

  const avgPerCall = totals && totals.calls > 0
    ? totals.cost_total_usd / totals.calls
    : 0

  const uniqueCount = totals
    ? tab === "property" ? totals.unique_properties
      : tab === "surface" ? totals.unique_surfaces
      : totals.unique_actors
    : 0

  const uniqueLabel = tab === "property" ? "Properties" : tab === "surface" ? "Surfaces" : "Users"

  return (
    <div className="ops-admin-page">
      <div className="page-header ops-page-header">
        <div className="ops-page-header__row">
          <h1 className="page-header__title" style={{ marginBottom: 0 }}>Cost Breakdown</h1>
          <span className="ops-badge">Ops</span>
        </div>
        <p className="page-header__subtitle">
          Granular LLM spend by property, surface, and user.
        </p>
      </div>

      {/* Date range chips */}
      <div className="ops-chip-row">
        {(["7d", "30d", "90d"] as RangePreset[]).map((p) => (
          <button
            key={p}
            type="button"
            className={`ops-filter-chip${p === preset ? " ops-filter-chip--active" : ""}`}
            onClick={() => setPreset(p)}
          >
            {p}
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="ops-cost-breakdown__tabs">
        {([
          { key: "property" as const, label: "By Property" },
          { key: "surface" as const, label: "By Surface" },
          { key: "user" as const, label: "By User" },
        ]).map((t) => (
          <button
            key={t.key}
            type="button"
            className={`ops-cost-breakdown__tab${t.key === tab ? " ops-cost-breakdown__tab--active" : ""}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <div className="ai-cc-error">{error}</div>}

      {loading ? (
        <div style={{ padding: "var(--sf-space-8)", textAlign: "center" }}>
          <div className="sf-spinner" style={{ margin: "0 auto" }} />
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="ops-summary-grid">
            <div className="ops-summary-card">
              <div className="ops-summary-card__title">Total Spend</div>
              <div className="ops-summary-card__value">{usd(totals?.cost_total_usd ?? 0)}</div>
            </div>
            <div className="ops-summary-card">
              <div className="ops-summary-card__title">Total Calls</div>
              <div className="ops-summary-card__value">{(totals?.calls ?? 0).toLocaleString()}</div>
            </div>
            <div className="ops-summary-card">
              <div className="ops-summary-card__title">{uniqueLabel}</div>
              <div className="ops-summary-card__value">{uniqueCount}</div>
            </div>
            <div className="ops-summary-card">
              <div className="ops-summary-card__title">Avg $/Call</div>
              <div className="ops-summary-card__value">{usd4(avgPerCall)}</div>
            </div>
          </div>

          {/* Breakdown table */}
          {rows.length === 0 ? (
            <div className="empty-state">
              <p>No usage data for this period.</p>
            </div>
          ) : (
            <div className="section-card" style={{ overflow: "auto" }}>
              <table className="data-table data-table--compact">
                <thead>
                  <tr>
                    {tab === "property" && (
                      <>
                        <th>Property</th>
                        <th style={{ textAlign: "right" }}>Calls</th>
                        <th style={{ textAlign: "right" }}>Tokens</th>
                        <th style={{ textAlign: "right" }}>Input $</th>
                        <th style={{ textAlign: "right" }}>Output $</th>
                        <th style={{ textAlign: "right" }}>Cache $</th>
                        <th style={{ textAlign: "right" }}>Total $</th>
                        <th style={{ width: 160 }}>Share</th>
                      </>
                    )}
                    {tab === "surface" && (
                      <>
                        <th>Surface</th>
                        <th>Category</th>
                        <th style={{ textAlign: "right" }}>Calls</th>
                        <th style={{ textAlign: "right" }}>Tokens</th>
                        <th style={{ textAlign: "right" }}>Input $</th>
                        <th style={{ textAlign: "right" }}>Output $</th>
                        <th style={{ textAlign: "right" }}>Total $</th>
                        <th style={{ width: 160 }}>Share</th>
                      </>
                    )}
                    {tab === "user" && (
                      <>
                        <th>User</th>
                        <th>Type</th>
                        <th>Email</th>
                        <th style={{ textAlign: "right" }}>Calls</th>
                        <th style={{ textAlign: "right" }}>Total $</th>
                        <th style={{ width: 160 }}>Share</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key + (row as UserRow).actor_type}>
                      {tab === "property" && (
                        <>
                          <td>{row.label || row.key}</td>
                          <td style={{ textAlign: "right" }}>{row.calls.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{fmtTokens(row.input_tokens + row.output_tokens)}</td>
                          <td style={{ textAlign: "right" }}>{usd4(row.cost_input_usd)}</td>
                          <td style={{ textAlign: "right" }}>{usd4(row.cost_output_usd)}</td>
                          <td style={{ textAlign: "right" }}>{usd4(row.cost_cache_usd)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{usd(row.cost_total_usd)}</td>
                          <td><SparkBar pct={row.pct_of_total} /></td>
                        </>
                      )}
                      {tab === "surface" && (
                        <>
                          <td>{SURFACE_LABELS[row.key] || row.label || row.key}</td>
                          <td>
                            <span className={`ops-cost-breakdown__category ops-cost-breakdown__category--${(SURFACE_CATEGORIES[row.key] || "ops").toLowerCase()}`}>
                              {SURFACE_CATEGORIES[row.key] || "Ops"}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>{row.calls.toLocaleString()}</td>
                          <td style={{ textAlign: "right" }}>{fmtTokens(row.input_tokens + row.output_tokens)}</td>
                          <td style={{ textAlign: "right" }}>{usd4(row.cost_input_usd)}</td>
                          <td style={{ textAlign: "right" }}>{usd4(row.cost_output_usd)}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{usd(row.cost_total_usd)}</td>
                          <td><SparkBar pct={row.pct_of_total} /></td>
                        </>
                      )}
                      {tab === "user" && (
                        <>
                          <td>{row.label || row.key}</td>
                          <td>
                            <span className={`ops-cost-breakdown__actor-type ops-cost-breakdown__actor-type--${(row as UserRow).actor_type || "system"}`}>
                              {(row as UserRow).actor_type || "system"}
                            </span>
                          </td>
                          <td className="ops-cost-breakdown__email">{(row as UserRow).email || "—"}</td>
                          <td style={{ textAlign: "right" }}>{row.calls.toLocaleString()}</td>
                          <td style={{ textAlign: "right", fontWeight: 600 }}>{usd(row.cost_total_usd)}</td>
                          <td><SparkBar pct={row.pct_of_total} /></td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SparkBar({ pct }: { pct: number }) {
  return (
    <div className="ops-cost-breakdown__spark-row">
      <div className="ai-cc-expenses-card__spark">
        <div
          className="ai-cc-expenses-card__spark-fill"
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="ops-cost-breakdown__pct">{pct.toFixed(1)}%</span>
    </div>
  )
}
